// src/components/Spreadsheet/GroupedSpreadsheetRow.jsx

import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

import GroupedSpreadsheetCell from './GroupedSpreadsheetCell';

import {
  areCellAddressesEqual,
  getColumnKey,
  getColumnWidth,
  getColumnWidthKey,
  getRowId,
  getVisibleColumns,
  normalizeString,
} from './groupedSpreadsheetUtils';

/**
 * GroupedSpreadsheetRow
 * -----------------------------------------------------------------------------
 * Row-segment renderer for the shared grouped spreadsheet engine.
 *
 * PURPOSE
 *  • Render one row segment for one group inside the unified sheet row
 *  • Keep row rendering domain-agnostic
 *  • Delegate actual cell rendering/editing to GroupedSpreadsheetCell
 *  • Support sparse re-rendering:
 *      - only selected rows should re-render when selection changes
 *      - only editing rows should re-render when edit mode changes
 *      - only dirty rows should re-render when dirty cell markers change
 *      - only rows with changed row objects should re-render on edits
 *
 * UNIFIED SHEET MODEL
 *  • GroupedSpreadsheetSheet owns the single row-number rail.
 *  • This row component renders only the cells for one group.
 *  • Multiple GroupedSpreadsheetRow instances can sit side-by-side inside
 *    one visual spreadsheet row.
 *
 * ENTERPRISE SPREADSHEET FOCUS MODEL
 *  • The canvas owns keyboard focus in view mode.
 *  • Cells are visual/selectable surfaces in view mode.
 *  • Editing mode is controlled by the canvas and passed down as editingCell.
 *  • This row only routes editing props to the correct cell.
 *
 * VIRTUALIZATION-READY DESIGN
 *  • Does not rely on DOM index for identity
 *  • Uses stable groupId + rowId + columnKey addressing
 *  • Accepts `style` so a virtualizer can position the row later
 *  • Does not own row state
 *  • Does not assume neighboring rows are mounted
 *  • Does not read/write storage, context, or Firestore
 *
 * IMPORTANT
 *  • Business rules belong in adapters/pages, not here.
 *  • Form Lists and SmartLists can both use this row renderer.
 * -----------------------------------------------------------------------------
 */

const DEFAULT_ROW_HEIGHT = 24;

const isCellSelected = ({
  selectedCell,
  groupId,
  rowId,
  columnKey,
}) => {
  if (!selectedCell) return false;

  return areCellAddressesEqual(selectedCell, {
    groupId,
    rowId,
    columnKey,
  });
};

const isSelectedRow = ({
  selectedCell,
  groupId,
  rowId,
}) => {
  if (!selectedCell) return false;

  return (
    normalizeString(selectedCell?.groupId, '') ===
      normalizeString(groupId, '') &&
    normalizeString(selectedCell?.rowId, '') ===
      normalizeString(rowId, '')
  );
};

const isCellEditing = ({
  editingCell,
  groupId,
  rowId,
  columnKey,
}) => {
  if (!editingCell) return false;

  return (
    normalizeString(editingCell?.groupId, '') ===
      normalizeString(groupId, '') &&
    normalizeString(editingCell?.rowId, '') ===
      normalizeString(rowId, '') &&
    normalizeString(editingCell?.columnKey, '') ===
      normalizeString(columnKey, '')
  );
};

const isEditingRow = ({
  editingCell,
  groupId,
  rowId,
}) => {
  if (!editingCell) return false;

  return (
    normalizeString(editingCell?.groupId, '') ===
      normalizeString(groupId, '') &&
    normalizeString(editingCell?.rowId, '') ===
      normalizeString(rowId, '')
  );
};

const areSetsEqual = (a, b) => {
  if (a === b) return true;

  if (!(a instanceof Set) || !(b instanceof Set)) {
    return false;
  }

  if (a.size !== b.size) return false;

  for (const value of a) {
    if (!b.has(value)) return false;
  }

  return true;
};

const areColumnWidthsEqualForColumns = ({
  groupId,
  columns,
  prevWidths,
  nextWidths,
}) => {
  if (prevWidths === nextWidths) return true;

  const visibleColumns = getVisibleColumns(columns);

  return visibleColumns.every((column) => {
    const columnKey = getColumnKey(column);
    if (!columnKey) return true;

    const scopedWidthKey = getColumnWidthKey({
      groupId,
      column,
      columnKey,
    });

    return (
      prevWidths?.[columnKey] === nextWidths?.[columnKey] &&
      prevWidths?.[scopedWidthKey] === nextWidths?.[scopedWidthKey]
    );
  });
};

const resolveCellReadOnly = ({
  readOnly,
  rowReadOnly,
  isCellReadOnly,
  group,
  groupId,
  row,
  rowId,
  rowIndex,
  column,
  columnKey,
  columnIndex,
}) => {
  if (readOnly === true || rowReadOnly === true) {
    return true;
  }

  if (column?.readOnly === true) {
    return true;
  }

  if (typeof isCellReadOnly === 'function') {
    return isCellReadOnly({
      group,
      groupId,
      row,
      rowId,
      rowIndex,
      column,
      columnKey,
      columnIndex,
    }) === true;
  }

  return false;
};

const resolveCellDisabled = ({
  disabled,
  rowDisabled,
  isCellDisabled,
  group,
  groupId,
  row,
  rowId,
  rowIndex,
  column,
  columnKey,
  columnIndex,
}) => {
  if (disabled === true || rowDisabled === true) {
    return true;
  }

  if (column?.disabled === true) {
    return true;
  }

  if (typeof isCellDisabled === 'function') {
    return isCellDisabled({
      group,
      groupId,
      row,
      rowId,
      rowIndex,
      column,
      columnKey,
      columnIndex,
    }) === true;
  }

  return false;
};

const resolveCellDirty = ({
  dirtyCellKeys,
  isCellDirty,
  group,
  groupId,
  row,
  rowId,
  rowIndex,
  column,
  columnKey,
  columnIndex,
}) => {
  if (dirtyCellKeys instanceof Set && dirtyCellKeys.has(columnKey)) {
    return true;
  }

  if (typeof isCellDirty === 'function') {
    return isCellDirty({
      group,
      groupId,
      row,
      rowId,
      rowIndex,
      column,
      columnKey,
      columnIndex,
    }) === true;
  }

  return false;
};

const GroupedSpreadsheetRow = memo(({
  group = null,
  groupId,
  row,
  rowIndex,
  columns,
  columnWidths = {},
  selectedCell = null,
  editingCell = null,
  dirtyCellKeys = null,
  showDirtyCellIndicator = true,
  rowHeight = DEFAULT_ROW_HEIGHT,
  readOnly = false,
  disabled = false,
  rowReadOnly = false,
  rowDisabled = false,
  rowIdKey = 'rowId',
  style = null,
  sx = null,
  registerCellRef,
  onSelectCell,
  onCommitCellValue,
  onRequestCellEdit,
  onEndCellEdit,
  onEditInputReady,
  onKeyDown,
  onPaste,
  renderCustomCell,
  isCellReadOnly,
  isCellDisabled,
  isCellDirty,
}) => {
  const rowId = useMemo(
    () => getRowId(row, rowIdKey),
    [row, rowIdKey],
  );

  const visibleColumns = useMemo(
    () => getVisibleColumns(columns),
    [columns],
  );

  const rowSx = useMemo(() => ({
    display: 'flex',
    alignItems: 'stretch',
    minHeight: rowHeight,
    height: rowHeight,
    boxSizing: 'border-box',

    // IMPORTANT:
    // This row may represent a group segment inside one unified sheet row.
    // It must size to its cells only. If this is minWidth: '100%', every
    // group segment consumes a full sheet width and creates large blank gaps
    // before the next group.
    width: 'fit-content',
    minWidth: 0,
    flexShrink: 0,

    backgroundColor: 'background.paper',
    ...(typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
  }), [rowHeight, sx]);

  if (!row || !rowId || visibleColumns.length === 0) {
    return null;
  }

  return (
    <Box
      role="row"
      data-group-id={groupId}
      data-row-id={rowId}
      data-row-index={rowIndex}
      style={style || undefined}
      sx={rowSx}
    >
      {visibleColumns.map((column, columnIndex) => {
        const columnKey = getColumnKey(column);
        if (!columnKey) return null;

        const width = getColumnWidth({
          groupId,
          column,
          columnWidths,
          fallback: 100,
        });

        const cellReadOnly = resolveCellReadOnly({
          readOnly,
          rowReadOnly,
          isCellReadOnly,
          group,
          groupId,
          row,
          rowId,
          rowIndex,
          column,
          columnKey,
          columnIndex,
        });

        const cellDisabled = resolveCellDisabled({
          disabled,
          rowDisabled,
          isCellDisabled,
          group,
          groupId,
          row,
          rowId,
          rowIndex,
          column,
          columnKey,
          columnIndex,
        });

        const selected = isCellSelected({
          selectedCell,
          groupId,
          rowId,
          columnKey,
        });

        const editing = isCellEditing({
          editingCell,
          groupId,
          rowId,
          columnKey,
        });

        const dirty = resolveCellDirty({
          dirtyCellKeys,
          isCellDirty,
          group,
          groupId,
          row,
          rowId,
          rowIndex,
          column,
          columnKey,
          columnIndex,
        });

        return (
          <GroupedSpreadsheetCell
            key={`${rowId}-${columnKey}`}
            groupId={groupId}
            row={row}
            rowId={rowId}
            rowIndex={rowIndex}
            column={column}
            columnIndex={columnIndex}
            value={row?.[columnKey]}
            width={width}
            selected={selected}
            readOnly={cellReadOnly}
            disabled={cellDisabled}
            dirty={dirty}
            showDirtyCellIndicator={showDirtyCellIndicator}
            editing={editing}
            editInitialValue={
              editing ? editingCell?.initialValue ?? null : null
            }
            registerCellRef={registerCellRef}
            onSelectCell={onSelectCell}
            onCommitCellValue={onCommitCellValue}
            onRequestCellEdit={onRequestCellEdit}
            onEndCellEdit={onEndCellEdit}
            onEditInputReady={onEditInputReady}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            renderCustomCell={renderCustomCell}
          />
        );
      })}
    </Box>
  );
}, (prev, next) => {
  const prevRowId = getRowId(prev.row, prev.rowIdKey);
  const nextRowId = getRowId(next.row, next.rowIdKey);

  if (prev.groupId !== next.groupId) return false;

  /**
   * Group metadata is part of the row render contract.
   *
   * WHY
   *  • Adapters can use group-level metadata to determine read-only, disabled,
   *    dirty, custom-cell, or governance behavior.
   *  • FormLists uses this to classify SmartList/org-managed option value cells
   *    as read-only while leaving dependency cells available.
   *  • If the group object changes but the row object does not, the row still
   *    needs to re-render so cell policy can update correctly.
   */
  if (prev.group !== next.group) return false;

  if (prevRowId !== nextRowId) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.row !== next.row) return false;
  if (prev.columns !== next.columns) return false;
  if (prev.rowHeight !== next.rowHeight) return false;
  if (prev.showDirtyCellIndicator !== next.showDirtyCellIndicator) return false;
  if (prev.readOnly !== next.readOnly) return false;
  if (prev.disabled !== next.disabled) return false;
  if (prev.rowReadOnly !== next.rowReadOnly) return false;
  if (prev.rowDisabled !== next.rowDisabled) return false;
  if (prev.style !== next.style) return false;
  if (prev.sx !== next.sx) return false;

  if (prev.registerCellRef !== next.registerCellRef) return false;
  if (prev.onSelectCell !== next.onSelectCell) return false;
  if (prev.onCommitCellValue !== next.onCommitCellValue) return false;
  if (prev.onRequestCellEdit !== next.onRequestCellEdit) return false;
  if (prev.onEndCellEdit !== next.onEndCellEdit) return false;
  if (prev.onEditInputReady !== next.onEditInputReady) return false;
  if (prev.onKeyDown !== next.onKeyDown) return false;
  if (prev.onPaste !== next.onPaste) return false;
  if (prev.renderCustomCell !== next.renderCustomCell) return false;
  if (prev.isCellReadOnly !== next.isCellReadOnly) return false;
  if (prev.isCellDisabled !== next.isCellDisabled) return false;
  if (prev.isCellDirty !== next.isCellDirty) return false;

  if (!areSetsEqual(prev.dirtyCellKeys, next.dirtyCellKeys)) {
    return false;
  }

  if (!areColumnWidthsEqualForColumns({
    groupId: next.groupId,
    columns: next.columns,
    prevWidths: prev.columnWidths,
    nextWidths: next.columnWidths,
  })) {
    return false;
  }

  const prevWasSelected = isSelectedRow({
    selectedCell: prev.selectedCell,
    groupId: prev.groupId,
    rowId: prevRowId,
  });

  const nextIsSelected = isSelectedRow({
    selectedCell: next.selectedCell,
    groupId: next.groupId,
    rowId: nextRowId,
  });

  if (prevWasSelected !== nextIsSelected) {
    return false;
  }

  if (prevWasSelected && nextIsSelected) {
    const prevColumnKey = normalizeString(prev.selectedCell?.columnKey, '');
    const nextColumnKey = normalizeString(next.selectedCell?.columnKey, '');

    if (prevColumnKey !== nextColumnKey) {
      return false;
    }
  }

  const prevWasEditing = isEditingRow({
    editingCell: prev.editingCell,
    groupId: prev.groupId,
    rowId: prevRowId,
  });

  const nextIsEditing = isEditingRow({
    editingCell: next.editingCell,
    groupId: next.groupId,
    rowId: nextRowId,
  });

  if (prevWasEditing !== nextIsEditing) {
    return false;
  }

  if (prevWasEditing && nextIsEditing) {
    const prevColumnKey = normalizeString(prev.editingCell?.columnKey, '');
    const nextColumnKey = normalizeString(next.editingCell?.columnKey, '');
    const prevInitialValue = prev.editingCell?.initialValue;
    const nextInitialValue = next.editingCell?.initialValue;

    if (prevColumnKey !== nextColumnKey) {
      return false;
    }

    if (prevInitialValue !== nextInitialValue) {
      return false;
    }
  }

  return true;
});

GroupedSpreadsheetRow.displayName = 'GroupedSpreadsheetRow';

GroupedSpreadsheetRow.propTypes = {
  group: PropTypes.object,
  groupId: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnWidths: PropTypes.object,
  selectedCell: PropTypes.shape({
    groupId: PropTypes.string,
    rowId: PropTypes.string,
    columnKey: PropTypes.string,
  }),
  editingCell: PropTypes.shape({
    groupId: PropTypes.string,
    rowId: PropTypes.string,
    columnKey: PropTypes.string,
    initialValue: PropTypes.any,
  }),
  dirtyCellKeys: PropTypes.instanceOf(Set),
  showDirtyCellIndicator: PropTypes.bool,
  rowHeight: PropTypes.number,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  rowReadOnly: PropTypes.bool,
  rowDisabled: PropTypes.bool,
  rowIdKey: PropTypes.string,
  style: PropTypes.object,
  sx: PropTypes.object,
  registerCellRef: PropTypes.func,
  onSelectCell: PropTypes.func,
  onCommitCellValue: PropTypes.func,
  onRequestCellEdit: PropTypes.func,
  onEndCellEdit: PropTypes.func,
  onEditInputReady: PropTypes.func,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  renderCustomCell: PropTypes.func,
  isCellReadOnly: PropTypes.func,
  isCellDisabled: PropTypes.func,
  isCellDirty: PropTypes.func,
};

export default GroupedSpreadsheetRow;